import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import RulesEngine from "../components/RulesEngine";
import { ArrowLeft, Plus, Trash } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [profile, setProfile] = useState({ name: "", email: "", phone: "", address: "" });
  const [contact, setContact] = useState({ name: "", email: "", phone: "", role: "" });

  const load = async () => {
    try {
      const { data } = await api.get(`/clients/${id}`);
      setClient(data);
      setProfile({ name: data.name, email: data.email || "", phone: data.phone || "", address: data.address || "" });
    } catch (e) { toast.error(fmtErr(e)); nav("/clients"); }
  };
  useEffect(() => { load(); }, [id]);

  const saveProfile = async () => {
    try {
      await api.patch(`/clients/${id}`, { ...profile, contacts: client.contacts || [], rules: client.rules || [] });
      toast.success("Profile saved"); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const addContact = async () => {
    if (!contact.name) return;
    try {
      await api.patch(`/clients/${id}`, { ...profile, contacts: [...(client.contacts || []), contact], rules: client.rules || [] });
      setContact({ name: "", email: "", phone: "", role: "" }); toast.success("Contact added"); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const removeContact = async (i) => {
    const contacts = (client.contacts || []).filter((_, xi) => xi !== i);
    await api.patch(`/clients/${id}`, { ...profile, contacts, rules: client.rules || [] }).catch((e) => toast.error(fmtErr(e)));
    load();
  };

  if (!client) return <div className="p-6 font-mono text-xs text-[var(--c-muted)] tracking-widest" data-testid="client-loading">LOADING CLIENT…</div>;

  return (
    <div className="p-6" data-testid="client-detail-page">
      <button onClick={() => nav("/clients")} data-testid="back-to-clients"
        className="flex items-center gap-1.5 text-xs text-[var(--c-muted)] hover:text-[var(--c-text)] transition-colors duration-150 mb-4">
        <ArrowLeft size={13} /> Clients
      </button>
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5">
        <h1 className="text-xl tracking-tight font-medium text-[var(--c-text)]" data-testid="client-name">{client.name}</h1>
        <div className="text-xs text-[var(--c-muted)] mt-1">{client.email || "No email"} · {client.projects?.length || 0} projects</div>
      </div>

      <Tabs defaultValue="profile" className="mt-5">
        <TabsList className="bg-[var(--c-surface)] border border-[var(--c-border)]" data-testid="client-tabs">
          {["Profile", "Contacts", "Projects", "Rules"].map((t) => (
            <TabsTrigger key={t} value={t.toLowerCase()} data-testid={`client-tab-${t.toLowerCase()}`}
              className="data-[state=active]:bg-[var(--c-border)] data-[state=active]:text-[var(--c-text)] text-[var(--c-muted)] text-xs">{t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="profile" className="mt-4">
          <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5 max-w-lg space-y-4" data-testid="profile-tab">
            {[["name", "Client name"], ["email", "Email"], ["phone", "Phone"], ["address", "Location / Address"]].map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">{label}</Label>
                <Input data-testid={`profile-${k}`} value={profile[k]}
                  onChange={(e) => setProfile({ ...profile, [k]: e.target.value })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" />
              </div>
            ))}
            <Button data-testid="profile-save" onClick={saveProfile}
              className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">Save Profile</Button>
          </div>
        </TabsContent>

        <TabsContent value="contacts" className="mt-4">
          <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5" data-testid="contacts-tab">
            {(client.contacts || []).map((c, i) => (
              <div key={i} className="flex items-center gap-4 py-2.5 border-b border-[var(--c-border)]/60 last:border-0" data-testid={`contact-row-${i}`}>
                <span className="text-xs font-medium text-[var(--c-text)] w-40">{c.name}</span>
                <span className="text-xs text-[var(--c-muted)] flex-1">{c.email || "—"}</span>
                <span className="font-mono text-xs text-[var(--c-muted)]">{c.phone || "—"}</span>
                <span className="text-xs text-[var(--c-muted)] w-28">{c.role || "—"}</span>
                <button onClick={() => removeContact(i)} className="p-1 text-[var(--c-muted)] hover:text-[var(--c-red)]"><Trash size={13} /></button>
              </div>
            ))}
            {(client.contacts || []).length === 0 && <div className="text-xs text-[var(--c-muted)] py-2">No contacts yet.</div>}
            <div className="flex items-center gap-2 mt-4">
              <Input data-testid="contact-name" placeholder="Name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })}
                className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
              <Input data-testid="contact-email" placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })}
                className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
              <Input data-testid="contact-phone" placeholder="Phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs w-36" />
              <Input data-testid="contact-role" placeholder="Role" value={contact.role} onChange={(e) => setContact({ ...contact, role: e.target.value })}
                className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs w-32" />
              <Button data-testid="add-contact-btn" onClick={addContact} size="sm"
                className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs"><Plus size={13} /></Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="projects" className="mt-4">
          <div className="border border-[var(--c-border)] rounded-sm bg-[var(--c-surface)]" data-testid="client-projects-tab">
            {(client.projects || []).map((p) => (
              <div key={p.project_code} onClick={() => nav(`/projects/${p.id}`)} data-testid={`client-project-${p.project_code}`}
                className="flex items-center gap-4 px-4 py-3 border-b border-[var(--c-border)]/60 last:border-0 hover:bg-[var(--c-border)]/25 cursor-pointer transition-colors duration-150">
                <span className="font-mono text-xs text-[var(--c-muted)]">{p.project_code}</span>
                <span className="text-xs text-[var(--c-text)] flex-1">{p.product_summary}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-sm bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)]">{p.stage.replace(/_/g, " ")}</span>
              </div>
            ))}
            {(client.projects || []).length === 0 && <div className="text-xs text-[var(--c-muted)] px-4 py-8 text-center">No projects for this client.</div>}
          </div>
        </TabsContent>

        <TabsContent value="rules" className="mt-4">
          <RulesEngine clientId={id} clientName={client.name} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
